<template>
  <div class="sub-menu" :style="getSubMenuStyle">
    <div class="sub-menu__header">
      <h1 class="sub-menu__header-title">
        {{ getTitle(getActiveSubMenu.lang) }}
      </h1>
      <div
        class="sub-menu__header-back"
        :class="{ 'sub-menu__header-back--active': isBackFocused }"
      >
        <Icon color="#f1f1f1" :height="32" :width="32"><ArrowLeft></ArrowLeft></Icon>
        {{ getBack }}
      </div>
    </div>
    <div class="sub-menu__content">
      <div class="sub-menu__preview">
        <div class="sub-menu__preview-thumbnail" v-if="getPreviewSource">
          <img class="preview-image" :src="getPreviewSource" alt="" />
        </div>
        <p class="sub-menu__preview-description">
          {{ getDescription(getActiveItem.lang || {}) }}
        </p>
      </div>
      <ul class="sub-menu__list" ref="listWrapper" :style="getStyleSheetOffset">
        <li
          v-for="(el, index) in getActiveSubMenuItems"
          :key="'event-' + index"
          class="sub-menu__list-element"
          :class="{
            '--active':
              !isBackFocused && index === getActiveSubMenuElementIndex,
          }"
          ref="listElements"
          @click="() => handleItemClickSelect(index)"
        >
          <div class="sub-menu__list-element-icon" v-if="el.icon">
            <img :src="el.icon" :alt="el.name" />
          </div>
          <div class="sub-menu__list-element-name">{{ getTitle(el.lang) }}</div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import router from '@/router';
import { keyHandler, KEYS } from '@/mixins/keyHandler';
import store from '@/store';
import { MEDIA_TYPE } from '@/helpers/MediaHandler';
import { getLang } from '@/plugin/LangControl';
import Icon from '@/components/Icon.vue';
import ArrowLeft from '@/assets/icons/WayfinderArrowLeft.vue';
import { NAV_TYPE } from '@/store/modules/navigation';

export default {
  components: { Icon, ArrowLeft },
  mixins: [keyHandler],
  props: {
    headline: {
      type: String,
      default: 'Sub Menu Headline',
    },
    backgroundImage: {
      type: String,
    },
    backgroundColor: {
      type: String,
    },
    textColor: {
      type: String,
      default: 'white',
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  emits: {},
  data() {
    return {
      activeElement: 0,
      scrollPositionCounter: 0,
      visibleElements: 0,
      wrapperHeight: 0,
      elementHeight: 0,
      scrollCounterOffset: 3,
      isBackFocused: false,
    };
  },
  mounted() {
    if (this.$refs.listElements)
      this.elementHeight = this.$refs.listElements[0].offsetHeight;
    if (this.$refs.listWrapper)
      this.wrapperHeight = this.$refs.listWrapper.offsetHeight;
    if (this.$refs.listElements && this.$refs.listWrapper) {
      this.visibleElements = Math.floor(this.wrapperHeight / this.elementHeight);
    } else {
      this.visibleElements = 0;
    }
    if (this.visibleElements - this.scrollCounterOffset <= 0) {
      this.scrollCounterOffset = 1;
    } else if (
      this.visibleElements - this.scrollCounterOffset <
      this.scrollCounterOffset
    ) {
      this.scrollCounterOffset =
        this.scrollCounterOffset -
        (this.visibleElements - this.scrollCounterOffset);
    }
  },
  beforeRouteUpdate: (to, from, next) => {
    if (!to.params.id) {
      next({ name: 'Home' });
    }
    store.dispatch('navigation/fetchSubMenu', to.params);
    next();
  },
  methods: {
    handleSubmit() {
      this.$store.dispatch('navigation/handleSubMenuAction');
    },
    eventHandler(key, event) {
      switch (key) {
        case KEYS.UP:
          if (this.getIsElementFirst && !this.isBackFocused) {
            this.isBackFocused = true;
          } else if (this.getIsElementFirst && this.isBackFocused) {
            this.isBackFocused = false;
            this.$store.dispatch('navigation/decSubMenu');
          } else if (this.isBackFocused) {
            this.isBackFocused = false;
          } else {
            this.$store.dispatch('navigation/decSubMenu');
          }
          break;
        case KEYS.DOWN:
          if (this.getIsElementLast && !this.isBackFocused) {
            this.isBackFocused = true;
          } else if (this.getIsElementLast && this.isBackFocused) {
            this.isBackFocused = false;
            this.$store.dispatch('navigation/incSubMenu');
          } else if (this.isBackFocused) {
            this.isBackFocused = false;
          } else {
            this.$store.dispatch('navigation/incSubMenu');
          }
          break;
        case KEYS.ENTER:
          if (!this.isBackFocused) {
            this.handleSubmit();
            break;
          }
        case KEYS.BACK:
          event.preventDefault();
          event.stopPropagation();
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
        default:
          console.log('NO KEY MATCH FOND: ', event, key);
      }
    },
    handleItemClickSelect(index) {
      this.activeElement = index;
      this.$nextTick(() => {
        this.handleSubmit();
      });
    },
    getTitleDescription(context) {
      return getLang(context);
    },
    getTitle(context) {
      return this.getTitleDescription(context).title;
    },
    getDescription(context) {
      return this.getTitleDescription(context).description;
    },
  },
  computed: {
    getBack() {
      return this.$i18n('back.name');
    },
    getActiveSubMenuElementIndex: {
      get() {
        return this.$store.getters['navigation/getActiveSubMenuElementIndex'];
      },
    },
    getIsElementFirst() {
      return 0 === this.getActiveSubMenuElementIndex;
    },
    getIsElementLast() {
      return (
        this.getActiveSubMenuItems.length - 1 ===
        this.getActiveSubMenuElementIndex
      );
    },
    getActiveSubMenu: {
      get() {
        return this.$store.getters['navigation/getActiveSubMenu'];
      },
    },
    getBackground() {
      return this.$store.getters['navigation/getActiveSubMenuBackground'];
    },
    getActiveSubMenuItems: {
      get() {
        return this.getActiveSubMenu.items;
      },
    },
    getActiveItem() {
      return this.$store.getters['navigation/getActiveSubMenuItem'];
    },

    getPreviewSource: {
      get() {
        return this.getActiveItem.thumbnail || this.getActiveItem.thumbUrl;
      },
    },

    getScrollPosition: {
      get() {
        return this.getScrollPositionCounter * this.elementHeight;
      },
    },
    getStyleSheetOffset: {
      get() {
        return { transform: `translateY(-${this.getScrollPosition}px)` };
      },
    },
    getSubMenuStyle: {
      get() {
        const styles = {
          color: this.textColor,
        };
        if (this.getBackground)
          styles.backgroundImage = `url(${this.getBackground})`;
        if (this.backgroundColor) styles.backgroundColor = this.backgroundColor;
        else styles.backgroundColor = 'black';
        return styles;
      },
    },
    getScrollPositionCounter() {
      const initialOffset = this.visibleElements - this.scrollCounterOffset;
      if (this.getActiveSubMenuElementIndex < initialOffset) {
        return 0;
      }
      if (this.getActiveSubMenuElementIndex > this.getActiveSubMenuItems.length - 1 - this.scrollCounterOffset) {
        return this.getActiveSubMenuItems.length - 1 - this.scrollCounterOffset;

        // return (
        //   this.getActiveSubMenuItems.length -
        //   initialOffset -
        //   this.scrollCounterOffset
        // );
      }
      return this.getActiveSubMenuElementIndex - initialOffset;
    },
  },
};
</script>

<style scoped>
.sub-menu {
  background: black;
  font-family: 'Helvetica Neue', HelveticaNeue, Helvetica-Neue, Helvetica,
    'BBAlpha Sans', sans-serif;
  height: 100vh;
  width: 100vw;
  padding: 1vh;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.sub-menu__list {
  color: white;
  text-align: left;
  flex: 1 1 50%;
  transition: transform ease 0.5s;
}

.sub-menu__list-element {
  align-items: center;
  border: 3px solid transparent;
  display: flex;
  font-size: 2.75vh;
  line-height: 3vh;
  list-style: none;
  padding: 1.5vh;
}
.sub-menu__list-element-icon {
  margin-right: 19px;
  height: 48px;
  width: 60px;
}

.sub-menu__headline {
  color: white;
  font-size: 4vh;
  text-transform: uppercase;
  padding: 3vh 0 4vw;
}

.sub-menu__content {
  display: flex;
  overflow: hidden;
}

.sub-menu__preview {
  flex: 1 1 50%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 50%;
}

.--active {
  border: 3px solid white;
}

.sub-menu__preview-description {
  font-size: 2.5vh;
  padding: 0 2.5vw;
  padding-top: 2vh;
  color: white;
  text-align: left;
  line-height: 3.5vh;
}

.sub-menu__preview-thumbnail {
  text-align: center;
  padding: 0 2vh;
  height: 30vh;
  width: 100%;
}

.preview-image {
  height: 100%;
  margin: 0 auto;
  object-fit: contain;
  width: 100%;
}

.sub-menu__header {
  align-items: center;
  display: flex;
  justify-content: center;
  height: 5vh;
  font-size: 2vw;
  position: relative;
  margin-bottom: 1rem;
}
.sub-menu__header-back {
  border: 2px solid transparent;
  padding: 1vh 1.5vw;
  position: absolute;
  right: 0;
  text-transform: uppercase;
}
.sub-menu__header-back--active {
  border-color: #f1f1f1;
}

.sub-menu__header-title {
  font-size: 3vw;
  font-weight: bold;
  text-transform: uppercase;
}
</style>



// WEBPACK FOOTER //
// src/pages/SubMenu.vue